import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import RezervasyonDetayModal from '../../components/RezervasyonDetayModal';
import TopluImportModal from '../../components/TopluImportModal';
import DatePicker from '../../components/DatePicker';
import { apiRequest } from '../../services/api';
import UsageInfo from '../../components/UsageInfo';
import LoadingSpinner from '../../components/LoadingSpinner';
import Pagination from '../../components/Pagination';
import DataTable, { Rozet } from '../../components/DataTable';

// ── Yardımcı Fonksiyonlar ──────────────────────────────────────────────────────
const formatTarih = (isoStr) => {
  if (!isoStr) return '';
  // Backend local zaman döndürür (Z yok) — doğrudan yerel metodlarla oku
  const d = new Date(isoStr);
  return `${String(d.getDate()).padStart(2,'0')}.${String(d.getMonth()+1).padStart(2,'0')}.${d.getFullYear()}`;
};

const geceHesapla = (giris, cikis) => {
  if (!giris || !cikis) return 0;
  return Math.round((new Date(cikis) - new Date(giris)) / (1000 * 60 * 60 * 24));
};

/**
 * Kolon başlığının altındaki filtre kutusu.
 *
 * Bu sayfalarda filtre TÜM liste üzerinde çalışıp sonra sayfalanıyor; bu yüzden
 * DataTable'ın kendi satır içi filtresi kullanılamıyor (o yalnızca eldeki
 * sayfayı süzer). Kutu `baslikAlti` ile başlığın altına konur.
 */
function FiltreKutusu({ children }) {
  return <div className="min-w-[120px]">{children}</div>;
}

const mapRezervasyon = (r) => ({
  id:           r.rezNo,
  rezervasyonId:r.rezervasyonId,
  checkIn:      r.odaCheckInYapildi,
  misafir:      r.rezervasyonMisafirler?.map(m => m.misafir?.adSoyad).filter(Boolean).join(' / ') || '-',
  oda:          r.odaNo || r.oda?.odaNo?.toString() || '-',
  odaId:        r.odaId,
  giris:        formatTarih(r.girisTarihi),
  gSaat:        r.girisSaati || '14:00',
  cikis:        r.cikisTarihi ? formatTarih(r.cikisTarihi) : 'Belirsiz',
  cSaat:        r.cikisSaati || '12:00',
  gece:         r.cikisTarihi ? geceHesapla(r.girisTarihi, r.cikisTarihi) : '-',
  acente:       r.acenteAd || r.acente?.acenteAd || r.acente?.ad || '',
  firma:        r.firmaAd || r.firma?.ad || '',
  durum:        r.durum || '',
  telefon:      r.rezervasyonMisafirler?.[0]?.misafir?.telefon || '',
  email:        r.rezervasyonMisafirler?.[0]?.misafir?.email || '',
  raw:          r,
});

const durumConfig = (durum) => {
  if (!durum) return { dot: 'bg-gray-400', cls: 'bg-gray-100 text-gray-600', gradient: 'from-slate-400 to-slate-500' };
  if (durum.includes('C-IN'))  return { dot: 'bg-emerald-500', cls: 'bg-emerald-100 text-emerald-700', gradient: 'from-emerald-500 to-teal-500' };
  if (durum.includes('C-OUT')) return { dot: 'bg-indigo-500',  cls: 'bg-indigo-100 text-indigo-700',   gradient: 'from-indigo-500 to-violet-500' };
  if (durum.includes('İptal')) return { dot: 'bg-rose-500',    cls: 'bg-rose-100 text-rose-600',       gradient: 'from-rose-500 to-red-500' };
  return { dot: 'bg-amber-400', cls: 'bg-amber-100 text-amber-700', gradient: 'from-amber-400 to-orange-400' };
};

const BOŞ_KOL = { rezNo:'', misafir:'', oda:'', giris:'', gSaat:'', cikis:'', cSaat:'', gece:'', acente:'', firma:'', durum:'' };
const BOŞ_KON = { adSoyad:'', ulke:'', telefon:'', cinsiyet:'', tc:'', oda:'', giris:'', cikis:'' };

const tabs = [
  { key: 'rezervasyonlar', label: 'Tüm Rezervasyonlar', icon: '◈', color: 'from-indigo-600 to-violet-600' },
  { key: 'girisler',       label: 'Giriş Bekleyen',      icon: '↓', color: 'from-amber-500 to-orange-500' },
  { key: 'cikislar',       label: 'Çıkışlar',            icon: '↑', color: 'from-violet-600 to-purple-600' },
  { key: 'checkin',        label: 'Check IN',             icon: '✓', color: 'from-emerald-500 to-teal-500' },
  { key: 'checkout',       label: 'Check OUT',            icon: '⇥', color: 'from-sky-500 to-indigo-500' },
  { key: 'konaklayanlar',  label: 'Konaklayanlar',        icon: '⊕', color: 'from-rose-500 to-pink-500' },
];

// ── Ortak Stiller ─────────────────────────────────────────────────────────────
const fi = "w-full bg-slate-50 border-2 border-transparent focus:border-indigo-400 focus:bg-white rounded-2xl px-3 py-2 text-[10px] font-black text-slate-700 uppercase tracking-widest outline-none transition-all placeholder-slate-300";
const LABEL = "text-[9px] font-black text-slate-400 uppercase tracking-[0.2em] block mb-1.5 ml-0.5";

// ── Avatar ────────────────────────────────────────────────────────────────────
function InitialsAvatar({ name, size = 'sm' }) {
  const parts = (name || '').split(' ').filter(Boolean);
  const initials = parts.length >= 2 ? parts[0][0] + parts[parts.length - 1][0] : (parts[0]?.[0] || '?');
  const colors = ['bg-violet-500','bg-indigo-500','bg-sky-500','bg-emerald-500','bg-amber-500','bg-rose-500'];
  const idx = (name?.charCodeAt(0) || 0) % colors.length;
  const sz = size === 'lg' ? 'w-11 h-11 text-sm' : 'w-8 h-8 text-xs';
  return (
    <span className={`inline-flex items-center justify-center ${sz} rounded-2xl text-white font-black flex-shrink-0 shadow-md ${colors[idx]}`}>
      {initials.toUpperCase()}
    </span>
  );
}

// ── İptal Onay Modal ──────────────────────────────────────────────────────────
function IptalOnayModal({ rezervasyon, onClose, onOnayla }) {
  return createPortal(
    <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-md z-[500] flex items-center justify-center p-4 animate-fade-in" onClick={onClose}>
      <div className="bg-white w-full max-w-md rounded-[2.5rem] shadow-2xl overflow-hidden animate-modal-in" onClick={e => e.stopPropagation()}>
        <div className="relative px-8 py-7 bg-gradient-to-r from-rose-600 to-red-600 overflow-hidden">
          <div className="absolute top-0 right-0 -mr-16 -mt-16 w-56 h-56 bg-white/10 rounded-full blur-3xl pointer-events-none" />
          <div className="relative flex items-center gap-4">
            <div className="w-12 h-12 bg-white/20 rounded-2xl flex items-center justify-center text-2xl shadow-inner border border-white/30">🔴</div>
            <div>
              <h2 className="font-black text-xl tracking-tight text-white uppercase leading-none">Rezervasyonu İptal Et</h2>
              <p className="text-white/60 text-[10px] font-black uppercase tracking-widest mt-1.5">Bu işlem geri alınamaz</p>
            </div>
          </div>
        </div>
        <div className="px-8 py-8">
          <p className="text-sm font-bold text-slate-600 mb-1">Aşağıdaki rezervasyonu iptal etmek istediğinize emin misiniz?</p>
          <p className="text-lg font-black text-slate-800 uppercase tracking-tight">{rezervasyon.misafir}</p>
          <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mt-1">Rez No: #{rezervasyon.id} · Oda {rezervasyon.oda}</p>
        </div>
        <div className="px-8 py-6 border-t border-gray-100 bg-gray-50/50 flex gap-3 justify-end">
          <button onClick={onClose} className="px-7 py-3.5 rounded-2xl border-2 border-gray-100 text-[10px] font-black text-gray-400 hover:bg-gray-100 transition-all uppercase tracking-widest">İPTAL</button>
          <button onClick={onOnayla} className="px-8 py-3.5 bg-gradient-to-r from-rose-600 to-red-600 hover:from-rose-700 hover:to-red-700 text-white rounded-2xl text-[10px] font-black shadow-xl shadow-rose-100 transition-all uppercase tracking-widest active:scale-95">
            🔴 REZERVASYONU İPTAL ET
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

// ── Ana Sayfa ─────────────────────────────────────────────────────────────────
export default function RezervasyonListesi() {
  const [detayRez, setDetayRez]         = useState(null);
  const [iptalRez, setIptalRez]         = useState(null);
  const [acikDropdown, setAcikDropdown] = useState(null);
  const [aktifTab, setAktifTab]         = useState('rezervasyonlar');
  const [giris, setGiris]               = useState('');
  const [cikis, setCikis]               = useState('');
  const [aktifFiltre, setAktifFiltre]   = useState({ giris: '', cikis: '' });
  const [dropdownPos, setDropdownPos]   = useState({ top: 0, bottom: 0, left: 0 });
  const [kolF, setKolF]                 = useState(BOŞ_KOL);
  const [konF, setKonF]                 = useState(BOŞ_KON);
  const [tumRezervasyonlar, setTumRezervasyonlar] = useState([]);
  const [yukleniyor, setYukleniyor]     = useState(true);
  const [hata, setHata]                 = useState(null);
  const [aktifSayfa, setAktifSayfa]     = useState(1);
  const [sayfaBasi, setSayfaBasi]       = useState(100);
  const [importModal, setImportModal]   = useState(false);

  const fetchRezervasyonlar = async () => {
    setYukleniyor(true); setHata(null);
    try {
      const data = await apiRequest('rezervasyonlar');
      if (data) setTumRezervasyonlar(data.map(mapRezervasyon));
    } catch { setHata('Rezervasyonlar yüklenirken hata oluştu.'); }
    setYukleniyor(false);
  };

  useEffect(() => { fetchRezervasyonlar(); }, []);

  const parseDate = (str) => {
    if (!str) return null;
    const [g, a, y] = str.split('.').map(Number);
    if (!g || !a || !y) return null;
    return new Date(y, a - 1, g);
  };

  const tarihFiltrele = (liste) => {
    const fGiris = parseDate(aktifFiltre.giris);
    const fCikis = parseDate(aktifFiltre.cikis);
    if (!fGiris || !fCikis) return liste;
    return liste.filter(r => {
      const rGiris = parseDate(r.giris);
      const rCikis = parseDate(r.cikis);
      if (!rGiris || !rCikis) return true;
      return rGiris >= fGiris && rCikis <= fCikis;
    });
  };

  const iceriyorMu = (alan, deger) => !deger || String(alan ?? '').toLocaleLowerCase('tr-TR').includes(deger.toLocaleLowerCase('tr-TR'));

  const kolonFiltrele = (liste) => liste.filter(r =>
    iceriyorMu(r.id,      kolF.rezNo)   &&
    iceriyorMu(r.misafir, kolF.misafir) &&
    iceriyorMu(r.oda,     kolF.oda)     &&
    iceriyorMu(r.giris,   kolF.giris)   &&
    iceriyorMu(r.gSaat,   kolF.gSaat)   &&
    iceriyorMu(r.cikis,   kolF.cikis)   &&
    iceriyorMu(r.cSaat,   kolF.cSaat)   &&
    iceriyorMu(r.gece,    kolF.gece)    &&
    iceriyorMu(r.acente,  kolF.acente)  &&
    iceriyorMu(r.firma,   kolF.firma)   &&
    iceriyorMu(r.durum,   kolF.durum)
  );

  const konaklayanFiltrele = (liste) => liste.filter(k =>
    iceriyorMu(k.adSoyad,  konF.adSoyad)  &&
    iceriyorMu(k.ulke,     konF.ulke)     &&
    iceriyorMu(k.telefon,  konF.telefon)  &&
    iceriyorMu(k.cinsiyet, konF.cinsiyet) &&
    iceriyorMu(k.tc,       konF.tc)       &&
    iceriyorMu(k.oda,      konF.oda)      &&
    iceriyorMu(k.giris,    konF.giris)    &&
    iceriyorMu(k.cikis,    konF.cikis)
  );

  const konaklayanlar = tumRezervasyonlar
    .filter(r => r.checkIn && !r.durum?.includes('C-OUT') && !r.durum?.includes('İptal'))
    .flatMap(r =>
      (r.raw?.rezervasyonMisafirler || []).map(rm => ({
        adSoyad:  rm.misafir?.adSoyad  || '-',
        ulke:     rm.misafir?.ulke     || 'TÜRKİYE',
        telefon:  rm.misafir?.telefon  || '',
        cinsiyet: rm.misafir?.cinsiyet || '',
        tc:       rm.misafir?.tcPasaportNo || rm.misafir?.pasaportNo || '',
        oda:      r.oda,
        giris:    `${r.giris} ${r.gSaat}`,
        cikis:    r.cikis === 'Belirsiz' ? 'Belirsiz' : `${r.cikis} ${r.cSaat}`,
        gece:     r.gece,
      }))
    );

  const getVeri = () => {
    let liste;
    switch (aktifTab) {
      case 'girisler':  liste = tumRezervasyonlar.filter(r => !r.checkIn && !r.durum?.includes('C-OUT') && !r.durum?.includes('İptal')); break;
      case 'cikislar':  liste = tumRezervasyonlar.filter(r => r.durum?.includes('C-OUT')); break;
      case 'checkin':   liste = tumRezervasyonlar.filter(r => r.checkIn && !r.durum?.includes('C-OUT')); break;
      case 'checkout':  liste = tumRezervasyonlar.filter(r => r.durum?.includes('C-OUT')); break;
      default:          liste = tumRezervasyonlar.filter(r => !r.durum?.includes('İptal'));
    }
    return kolonFiltrele(tarihFiltrele(liste));
  };

  const veriTum  = getVeri();
  const konVeri  = konaklayanFiltrele(konaklayanlar);
  const setKol   = (key, val) => { setKolF(f => ({ ...f, [key]: val })); setAktifSayfa(1); };
  const setKon   = (key, val) => setKonF(f => ({ ...f, [key]: val }));
  const kolFiltreVar = Object.values(kolF).some(v => v);
  const konFiltreVar = Object.values(konF).some(v => v);
  const veri     = veriTum.slice((aktifSayfa - 1) * sayfaBasi, aktifSayfa * sayfaBasi);

  const tabSayilari = {
    rezervasyonlar: tumRezervasyonlar.filter(r => !r.durum?.includes('İptal')).length,
    girisler:       tumRezervasyonlar.filter(r => !r.checkIn && !r.durum?.includes('C-OUT') && !r.durum?.includes('İptal')).length,
    cikislar:       tumRezervasyonlar.filter(r => r.durum?.includes('C-OUT')).length,
    checkin:        tumRezervasyonlar.filter(r => r.checkIn && !r.durum?.includes('C-OUT')).length,
    checkout:       tumRezervasyonlar.filter(r => r.durum?.includes('C-OUT')).length,
    konaklayanlar:  konaklayanlar.length,
  };

  const handleIslem = async (label, r) => {
    setAcikDropdown(null);
    if (label === 'detay') {
      setDetayRez(r);
    } else if (label === 'iptal') {
      setIptalRez(r);
    }
  };

  const aktifTabObj = tabs.find(t => t.key === aktifTab);

  // ── Loading & Error ──────────────────────────────────────────────────────────
  if (yukleniyor) return <LoadingSpinner fullPage text="Rezervasyonlar yükleniyor..." color="indigo" size="lg" />;

  if (hata) return (
    <div className="flex items-center justify-center h-64">
      <div className="text-center flex flex-col items-center gap-4">
        <div className="w-16 h-16 bg-rose-100 rounded-3xl flex items-center justify-center text-3xl">⚠</div>
        <p className="font-black text-slate-700 uppercase tracking-widest text-sm">{hata}</p>
        <button onClick={fetchRezervasyonlar} className="px-8 py-3.5 bg-gradient-to-r from-indigo-600 to-violet-600 text-white rounded-2xl text-[10px] font-black uppercase tracking-widest shadow-xl">
          Tekrar Dene
        </button>
      </div>
    </div>
  );

  return (
    <div className="p-4 sm:p-8 space-y-8 animate-fade-in">

      {/* ── Header ──────────────────────────────────────────────────────────── */}
      <div className="bg-white/95 backdrop-blur-xl rounded-[3rem] p-8 sm:p-10 shadow-2xl border border-white/40 overflow-hidden">
        <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-8">

          {/* Başlık */}
          <div className="flex items-center gap-3 sm:gap-6">
            <div className="w-12 h-12 sm:w-16 sm:h-16 bg-slate-900 rounded-[1.5rem] sm:rounded-[2rem] flex items-center justify-center text-white text-2xl sm:text-3xl shadow-2xl flex-shrink-0">◈</div>
            <div>
              <h1 className="text-2xl sm:text-3xl font-black text-slate-800 tracking-tighter uppercase leading-none mb-2">Rezervasyon Listesi</h1>
              <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">{tumRezervasyonlar.length} toplam rezervasyon</p>
            </div>
          </div>

          {/* Tarih Filtresi + Yenile */}
          <div className="flex flex-col sm:flex-row items-start sm:items-end gap-4">
            <div className="flex flex-wrap gap-3 items-end">
              <div>
                <label className={LABEL}>Giriş Tarihi</label>
                <DatePicker value={giris} onChange={val => {
                  setGiris(val);
                  const pd = s => { if (!s) return null; const [g,a,y] = s.split('.').map(Number); return new Date(y,a-1,g); };
                  if (pd(val) && pd(cikis) && pd(cikis) < pd(val)) setCikis(val);
                }} className="w-full bg-white border-2 border-slate-100 focus:border-indigo-400 rounded-2xl px-5 py-3 text-xs font-black text-slate-700 outline-none transition-all" />
              </div>
              <div>
                <label className={LABEL}>Çıkış Tarihi</label>
                <DatePicker value={cikis} onChange={setCikis} minDate={giris}
                  className="w-full bg-white border-2 border-slate-100 focus:border-indigo-400 rounded-2xl px-5 py-3 text-xs font-black text-slate-700 outline-none transition-all" />
              </div>
              <div className="flex gap-2 items-center">
                <button onClick={() => { setAktifFiltre({ giris, cikis }); setAktifSayfa(1); }}
                  className="h-[46px] px-6 bg-gradient-to-r from-indigo-600 to-violet-600 text-white rounded-2xl text-[10px] font-black uppercase tracking-widest shadow-xl shadow-indigo-100 transition-all hover:from-indigo-700 hover:to-violet-700 active:scale-95 whitespace-nowrap">
                  Filtrele
                </button>
                {(aktifFiltre.giris || aktifFiltre.cikis) && (
                  <button onClick={() => { setAktifFiltre({ giris:'', cikis:'' }); setGiris(''); setCikis(''); }}
                    className="h-[46px] px-4 border-2 border-rose-200 text-rose-500 hover:bg-rose-50 rounded-2xl text-[10px] font-black uppercase tracking-widest transition-all whitespace-nowrap">
                    ✕
                  </button>
                )}
                <button onClick={fetchRezervasyonlar}
                  className="w-[46px] h-[46px] bg-white border-2 border-slate-100 hover:border-indigo-400 rounded-2xl flex items-center justify-center shadow-lg transition-all text-slate-500 text-lg">
                  ↺
                </button>
                <button onClick={() => setImportModal(true)}
                  className="h-[46px] px-5 bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-700 hover:to-purple-700 text-white rounded-2xl text-[10px] font-black uppercase tracking-widest shadow-lg shadow-violet-200 transition-all active:scale-95 whitespace-nowrap flex items-center gap-2">
                  <span>📋</span> Toplu Import
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Stat Kartları */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-10">
          {[
            { label: 'Toplam Rez.',    v: tabSayilari.rezervasyonlar, c: 'from-slate-700 to-slate-900',   i: '◈' },
            { label: 'Giriş Bekleyen', v: tabSayilari.girisler,       c: 'from-amber-400 to-orange-500',  i: '↓' },
            { label: 'Konaklayanlar',  v: tabSayilari.konaklayanlar,  c: 'from-emerald-400 to-teal-600',  i: '⊕' },
            { label: 'Çıkış Yapıldı',  v: tabSayilari.cikislar,       c: 'from-violet-500 to-purple-600', i: '↑' },
          ].map(s => (
            <div key={s.label} className={`bg-gradient-to-br ${s.c} p-5 rounded-[2rem] text-white shadow-xl relative overflow-hidden group transition-all duration-300 hover:scale-105 hover:shadow-2xl hover:-translate-y-1 cursor-default`}>
              <div className="absolute top-0 right-0 -mr-8 -mt-8 w-24 h-24 bg-white/10 rounded-full blur-2xl group-hover:bg-white/20 transition-all duration-500" />
              <span className="absolute top-2 right-3 opacity-20 text-3xl group-hover:scale-110 group-hover:rotate-12 transition-all duration-500">{s.i}</span>
              <p className="text-[8px] font-black opacity-60 uppercase tracking-tight mb-1 relative z-10 leading-tight">{s.label}</p>
              <h3 className="text-2xl sm:text-3xl font-black tracking-tighter relative z-10">{s.v}</h3>
            </div>
          ))}
        </div>
      </div>

      {/* ── Tab Bar ─────────────────────────────────────────────────────────── */}
      <div className="bg-white/95 backdrop-blur-xl rounded-[2.5rem] p-4 shadow-2xl border border-white/40 overflow-hidden">
        <div className="flex items-center gap-2 overflow-x-auto pb-1" style={{ scrollbarWidth: 'none' }}>
          {tabs.map(tab => {
            const aktif = aktifTab === tab.key;
            return (
              <button key={tab.key}
                onClick={() => { setAktifTab(tab.key); setAktifSayfa(1); }}
                className={`relative flex items-center gap-2 px-5 py-2.5 rounded-2xl text-[10px] font-black uppercase tracking-widest transition-all duration-200 whitespace-nowrap flex-shrink-0
                  ${aktif
                    ? `bg-gradient-to-r ${tab.color} text-white shadow-lg`
                    : 'text-slate-500 hover:text-slate-800 hover:bg-slate-100'
                  }`}>
                <span className="text-xs">{tab.icon}</span>
                <span>{tab.label}</span>
                <span className={`text-[9px] font-black px-2 py-0.5 rounded-xl ml-0.5
                  ${aktif ? 'bg-white/20 text-white' : 'bg-slate-100 text-slate-500'}`}>
                  {tabSayilari[tab.key]}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* ── Tablo Kartı ─────────────────────────────────────────────────────── */}
      <div className="bg-white/95 backdrop-blur-xl rounded-[3rem] shadow-2xl border border-white/40 overflow-hidden">

        {/* Kart Başlık */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 px-8 py-6 border-b border-slate-100">
          <div className="flex items-center gap-3">
            <div className={`w-8 h-8 rounded-xl bg-gradient-to-br ${aktifTabObj?.color || 'from-slate-700 to-slate-900'} flex items-center justify-center text-white text-sm font-black`}>
              {aktifTabObj?.icon}
            </div>
            <h2 className="text-sm font-black text-slate-700 uppercase tracking-widest">
              {aktifTab === 'konaklayanlar' ? konVeri.length : veriTum.length} Kayıt
            </h2>
            {(aktifTab === 'konaklayanlar' ? konFiltreVar : kolFiltreVar) && (
              <span className="bg-indigo-100 text-indigo-700 text-[9px] font-black px-3 py-1 rounded-xl uppercase tracking-widest">Filtre Aktif</span>
            )}
            {(aktifFiltre.giris || aktifFiltre.cikis) && (
              <span className="bg-amber-100 text-amber-700 text-[9px] font-black px-3 py-1 rounded-xl uppercase tracking-widest">
                {aktifFiltre.giris} – {aktifFiltre.cikis}
              </span>
            )}
            {(aktifTab === 'konaklayanlar' ? konFiltreVar : kolFiltreVar) && (
              <button onClick={() => aktifTab === 'konaklayanlar' ? setKonF(BOŞ_KON) : setKolF(BOŞ_KOL)}
                className="text-[9px] font-black text-rose-500 hover:text-rose-700 uppercase tracking-widest transition-colors">✕ Temizle</button>
            )}
          </div>
        </div>

        {/* ── MOBİL ARAMA ── */}
        <div className="block md:hidden px-6 py-4 border-b border-slate-100">
          {aktifTab === 'konaklayanlar' ? (
            <div className="grid grid-cols-2 gap-2">
              {[['adSoyad','Ad Soyad'],['ulke','Ülke'],['telefon','Telefon'],['tc','TC / Pasaport']].map(([k,ph]) => (
                <input key={k} value={konF[k]} onChange={e => setKon(k, e.target.value)} placeholder={`${ph}...`} className={fi} />
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-2">
              <input value={kolF.rezNo}   onChange={e => setKol('rezNo',   e.target.value)} placeholder="Rez No..."   className={fi} />
              <input value={kolF.misafir} onChange={e => setKol('misafir', e.target.value)} placeholder="Misafir..."  className={fi} />
              <input value={kolF.oda}     onChange={e => setKol('oda',     e.target.value)} placeholder="Oda..."      className={fi} />
              <input value={kolF.durum}   onChange={e => setKol('durum',   e.target.value)} placeholder="Durum..."    className={fi} />
            </div>
          )}
        </div>

        {/* ── MOBİL KART LİSTESİ ── */}
        <div className="block md:hidden divide-y divide-slate-50">
          {aktifTab === 'konaklayanlar' ? (
            konVeri.length === 0 ? (
              <div className="text-center py-24 flex flex-col items-center gap-4">
                <div className="w-16 h-16 rounded-3xl bg-slate-100 flex items-center justify-center text-3xl">⊕</div>
                <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Konaklayan misafir bulunamadı</p>
              </div>
            ) : konVeri.map((k, i) => (
              <div key={i} className="px-6 py-5 hover:bg-indigo-50/30 transition-colors">
                <div className="flex items-center gap-3">
                  <InitialsAvatar name={k.adSoyad} />
                  <div>
                    <p className="font-black text-slate-800 text-sm uppercase tracking-tight">{k.adSoyad}</p>
                    <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                      <span className="font-mono text-[10px] text-slate-400">{k.tc || '—'}</span>
                      <span className="bg-indigo-100 text-indigo-700 px-2 py-0.5 rounded-lg font-black text-[9px] uppercase">Oda {k.oda}</span>
                    </div>
                  </div>
                </div>
                <div className="mt-3 grid grid-cols-2 gap-x-4 gap-y-1.5 text-[10px] ml-11">
                  <div><span className="text-slate-400 font-black uppercase">Ülke: </span><span className="font-black text-slate-700 uppercase">{k.ulke}</span></div>
                  <div><span className="text-slate-400 font-black uppercase">Cinsiyet: </span><span className="font-black text-slate-700 uppercase">{k.cinsiyet || '—'}</span></div>
                  <div><span className="text-slate-400 font-black uppercase">Giriş: </span><span className="font-black text-slate-600">{k.giris}</span></div>
                  <div><span className="text-slate-400 font-black uppercase">Çıkış: </span><span className={`font-black ${k.cikis === 'Belirsiz' ? 'text-amber-500' : 'text-slate-600'}`}>{k.cikis}</span></div>
                  {k.telefon && <div><span className="text-slate-400 font-black uppercase">Tel: </span><span className="font-black text-slate-600">{k.telefon}</span></div>}
                  <div><span className="text-slate-400 font-black uppercase">Gece: </span><span className="font-black text-slate-600">{k.gece === '-' ? 'Belirsiz' : `${k.gece} gece`}</span></div>
                </div>
              </div>
            ))
          ) : (
            veri.length === 0 ? (
              <div className="text-center py-24 flex flex-col items-center gap-4">
                <div className="w-16 h-16 rounded-3xl bg-slate-100 flex items-center justify-center text-3xl">◈</div>
                <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Kayıt bulunamadı</p>
              </div>
            ) : veri.map(r => {
              const dc = durumConfig(r.durum);
              return (
                <div key={r.id} className="px-6 py-5 hover:bg-indigo-50/30 transition-colors">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-center gap-3 min-w-0">
                      <InitialsAvatar name={r.misafir} />
                      <div className="min-w-0">
                        <p className="font-black text-slate-800 text-sm uppercase tracking-tight truncate">{r.misafir}</p>
                        <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                          <span className="font-mono text-[10px] text-slate-400">#{r.id}</span>
                          <span className="bg-indigo-100 text-indigo-700 px-2 py-0.5 rounded-lg font-black text-[9px] uppercase">Oda {r.oda}</span>
                          {r.checkIn && <span className="bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-lg font-black text-[9px] uppercase">C-IN ✓</span>}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <span className={`inline-flex items-center gap-1 text-[9px] font-black px-2.5 py-1 rounded-xl uppercase tracking-widest ${dc.cls}`}>
                        <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${dc.dot}`} />
                        {r.durum}
                      </span>
                      <button onClick={e => {
                        e.stopPropagation();
                        if (acikDropdown === r.id) { setAcikDropdown(null); return; }
                        const rect = e.currentTarget.getBoundingClientRect();
                        setDropdownPos({ top: rect.top, bottom: rect.bottom, left: rect.left });
                        setAcikDropdown(r.id);
                      }} className="w-9 h-9 flex items-center justify-center bg-slate-100 hover:bg-indigo-600 hover:text-white text-slate-600 rounded-xl transition-all">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><circle cx="12" cy="5" r="2"/><circle cx="12" cy="12" r="2"/><circle cx="12" cy="19" r="2"/></svg>
                      </button>
                      <DropdownMenu r={r} acikDropdown={acikDropdown} setAcikDropdown={setAcikDropdown} dropdownPos={dropdownPos} handleIslem={handleIslem} />
                    </div>
                  </div>
                  <div className="mt-3 grid grid-cols-2 gap-x-4 gap-y-1.5 text-[10px] ml-11">
                    <div><span className="text-slate-400 font-black uppercase">Giriş: </span><span className="font-black text-slate-600">{r.giris} {r.gSaat}</span></div>
                    <div><span className="text-slate-400 font-black uppercase">Çıkış: </span><span className={`font-black ${r.cikis === 'Belirsiz' ? 'text-amber-500' : 'text-slate-600'}`}>{r.cikis === 'Belirsiz' ? 'Belirsiz' : `${r.cikis} ${r.cSaat}`}</span></div>
                    <div><span className="text-slate-400 font-black uppercase">Gece: </span><span className="font-black text-slate-600">{r.gece === '-' ? 'Belirsiz' : `${r.gece} gece`}</span></div>
                    {r.acente && <div><span className="text-slate-400 font-black uppercase">Acente: </span><span className="font-black text-slate-600">{r.acente}</span></div>}
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* ── MASAÜSTÜ TABLO ── */}
        <div className="hidden md:block overflow-x-auto">
          {aktifTab === 'konaklayanlar' ? (

            /* Konaklayanlar Tablosu */
            <DataTable
              satirlar={konVeri}
              enAzGenislik="1150px"
              bosIkon="⊕"
              bosMetin="Konaklayan misafir bulunamadı"
              kolonlar={[
                {
                  anahtar: 'adSoyad', baslik: 'Ad Soyad', siralanir: true,
                  baslikAlti: () => (
                    <FiltreKutusu>
                      <input placeholder="Ad Soyad..." value={konF.adSoyad} onChange={e => setKon('adSoyad', e.target.value)} className={fi} />
                    </FiltreKutusu>
                  ),
                  ciz: k => (
                    <div className="flex items-center gap-3">
                      <InitialsAvatar name={k.adSoyad} />
                      <span className="font-black text-slate-800 text-xs uppercase tracking-tight">{k.adSoyad}</span>
                    </div>
                  ),
                },
                {
                  anahtar: 'ulke', baslik: 'Ülke', siralanir: true,
                  baslikAlti: () => (
                    <FiltreKutusu>
                      <input placeholder="Ülke..." value={konF.ulke} onChange={e => setKon('ulke', e.target.value)} className={fi} />
                    </FiltreKutusu>
                  ),
                  ciz: k => <Rozet renk="indigo">{k.ulke}</Rozet>,
                },
                {
                  anahtar: 'telefon', baslik: 'Telefon', siralanir: true,
                  baslikAlti: () => (
                    <FiltreKutusu>
                      <input placeholder="Telefon..." value={konF.telefon} onChange={e => setKon('telefon', e.target.value)} className={fi} />
                    </FiltreKutusu>
                  ),
                  ciz: k => <span className="text-slate-600 text-xs font-bold">{k.telefon || '—'}</span>,
                },
                {
                  anahtar: 'cinsiyet', baslik: 'Cinsiyet', hizala: 'orta',
                  baslikAlti: () => (
                    <FiltreKutusu>
                      <input placeholder="Cinsiyet..." value={konF.cinsiyet} onChange={e => setKon('cinsiyet', e.target.value)} className={fi} />
                    </FiltreKutusu>
                  ),
                  ciz: k => (
                    <Rozet renk={k.cinsiyet === 'Erkek' ? 'cyan' : k.cinsiyet === 'Kadın' ? 'rose' : 'gray'}>
                      {k.cinsiyet || '—'}
                    </Rozet>
                  ),
                },
                {
                  anahtar: 'tc', baslik: 'TC / Pasaport', siralanir: true,
                  baslikAlti: () => (
                    <FiltreKutusu>
                      <input placeholder="TC / Pasaport..." value={konF.tc} onChange={e => setKon('tc', e.target.value)} className={fi} />
                    </FiltreKutusu>
                  ),
                  ciz: k => <span className="font-mono text-slate-500 text-xs">{k.tc || '—'}</span>,
                },
                {
                  anahtar: 'oda', baslik: 'Oda', hizala: 'orta', siralanir: true,
                  baslikAlti: () => (
                    <FiltreKutusu>
                      <input placeholder="Oda..." value={konF.oda} onChange={e => setKon('oda', e.target.value)} className={fi} />
                    </FiltreKutusu>
                  ),
                  ciz: k => <Rozet renk="indigo">{k.oda}</Rozet>,
                },
                {
                  anahtar: 'giris', baslik: 'Giriş', siralanir: true,
                  baslikAlti: () => (
                    <FiltreKutusu>
                      <input placeholder="Giriş..." value={konF.giris} onChange={e => setKon('giris', e.target.value)} className={fi} />
                    </FiltreKutusu>
                  ),
                  ciz: k => <span className="text-slate-600 text-xs font-bold whitespace-nowrap">{k.giris}</span>,
                },
                {
                  anahtar: 'cikis', baslik: 'Çıkış', siralanir: true,
                  baslikAlti: () => (
                    <FiltreKutusu>
                      <input placeholder="Çıkış..." value={konF.cikis} onChange={e => setKon('cikis', e.target.value)} className={fi} />
                    </FiltreKutusu>
                  ),
                  ciz: k => k.cikis === 'Belirsiz'
                    ? <span className="text-amber-500 italic font-black text-xs">Belirsiz</span>
                    : <span className="text-slate-600 text-xs font-bold whitespace-nowrap">{k.cikis}</span>,
                },
                {
                  anahtar: 'gece', baslik: 'Gece', hizala: 'orta', siralanir: true,
                  ciz: k => <Rozet>{k.gece === '-' ? 'Belirsiz' : k.gece + ' gece'}</Rozet>,
                },
              ]}
            />

          ) : (

            /* Ana Rezervasyon Tablosu */
            <DataTable
              anahtarAlan="id"
              satirlar={veri}
              enAzGenislik="1550px"
              bosIkon="◈"
              bosMetin="Kayıt bulunamadı"
              kolonlar={[
                {
                  anahtar: 'id', baslik: 'Rez. No', siralanir: true,
                  baslikAlti: () => (
                    <FiltreKutusu>
                      <input placeholder="Rez. No..." value={kolF.rezNo} onChange={e => setKol('rezNo', e.target.value)} className={fi} />
                    </FiltreKutusu>
                  ),
                  ciz: r => <span className="font-mono font-black text-slate-500 text-[10px] bg-slate-100 px-2.5 py-1 rounded-xl">#{r.id}</span>,
                },
                {
                  anahtar: 'checkIn', baslik: 'C-IN', hizala: 'orta',
                  ciz: r => r.checkIn
                    ? <span className="inline-flex items-center justify-center w-7 h-7 bg-emerald-100 text-emerald-600 rounded-xl text-xs font-black">✓</span>
                    : <span className="inline-flex items-center justify-center w-7 h-7 bg-slate-100 text-slate-300 rounded-xl text-xs font-black">—</span>,
                },
                {
                  anahtar: 'misafir', baslik: 'Misafir', siralanir: true,
                  baslikAlti: () => (
                    <FiltreKutusu>
                      <input placeholder="Misafir..." value={kolF.misafir} onChange={e => setKol('misafir', e.target.value)} className={fi} />
                    </FiltreKutusu>
                  ),
                  ciz: r => (
                    <div className="flex items-center gap-3">
                      <InitialsAvatar name={r.misafir} />
                      <span className="font-black text-slate-800 text-xs uppercase tracking-tight whitespace-nowrap">{r.misafir}</span>
                    </div>
                  ),
                },
                {
                  anahtar: 'oda', baslik: 'Oda', hizala: 'orta', siralanir: true,
                  baslikAlti: () => (
                    <FiltreKutusu>
                      <input placeholder="Oda..." value={kolF.oda} onChange={e => setKol('oda', e.target.value)} className={fi} />
                    </FiltreKutusu>
                  ),
                  ciz: r => <Rozet renk="indigo">{r.oda}</Rozet>,
                },
                {
                  anahtar: 'giris', baslik: 'Giriş', siralanir: true,
                  baslikAlti: () => (
                    <FiltreKutusu>
                      <input placeholder="GG.AA.YY" value={kolF.giris} onChange={e => setKol('giris', e.target.value)} className={fi} />
                    </FiltreKutusu>
                  ),
                  ciz: r => <span className="text-slate-700 text-xs font-black whitespace-nowrap">{r.giris}</span>,
                },
                {
                  anahtar: 'gSaat', baslik: 'G. saat',
                  baslikAlti: () => (
                    <FiltreKutusu>
                      <input placeholder="HH:MM" value={kolF.gSaat} onChange={e => setKol('gSaat', e.target.value)} className={fi} />
                    </FiltreKutusu>
                  ),
                  ciz: r => <span className="text-slate-400 text-xs font-bold">{r.gSaat}</span>,
                },
                {
                  anahtar: 'cikis', baslik: 'Çıkış', siralanir: true,
                  baslikAlti: () => (
                    <FiltreKutusu>
                      <input placeholder="GG.AA.YY" value={kolF.cikis} onChange={e => setKol('cikis', e.target.value)} className={fi} />
                    </FiltreKutusu>
                  ),
                  ciz: r => r.cikis === 'Belirsiz'
                    ? <span className="text-amber-500 italic text-xs font-black">Belirsiz</span>
                    : <span className="text-slate-700 text-xs font-black whitespace-nowrap">{r.cikis}</span>,
                },
                {
                  anahtar: 'cSaat', baslik: 'Ç. saat',
                  baslikAlti: () => (
                    <FiltreKutusu>
                      <input placeholder="HH:MM" value={kolF.cSaat} onChange={e => setKol('cSaat', e.target.value)} className={fi} />
                    </FiltreKutusu>
                  ),
                  ciz: r => <span className="text-slate-400 text-xs font-bold">{r.cSaat}</span>,
                },
                {
                  anahtar: 'gece', baslik: 'Gece', hizala: 'orta', siralanir: true,
                  baslikAlti: () => (
                    <FiltreKutusu>
                      <input placeholder="Gece..." value={kolF.gece} onChange={e => setKol('gece', e.target.value)} className={fi} />
                    </FiltreKutusu>
                  ),
                  ciz: r => <Rozet>{r.gece === '-' ? 'Belirsiz' : r.gece + ' gece'}</Rozet>,
                },
                {
                  anahtar: 'acente', baslik: 'Acente', siralanir: true,
                  baslikAlti: () => (
                    <FiltreKutusu>
                      <input placeholder="Acente..." value={kolF.acente} onChange={e => setKol('acente', e.target.value)} className={fi} />
                    </FiltreKutusu>
                  ),
                  ciz: r => <span className="text-slate-500 text-xs font-bold whitespace-nowrap">{r.acente || '—'}</span>,
                },
                {
                  anahtar: 'firma', baslik: 'Firma', siralanir: true,
                  baslikAlti: () => (
                    <FiltreKutusu>
                      <input placeholder="Firma..." value={kolF.firma} onChange={e => setKol('firma', e.target.value)} className={fi} />
                    </FiltreKutusu>
                  ),
                  ciz: r => r.firma
                    ? <Rozet renk="violet">{r.firma}</Rozet>
                    : <span className="text-slate-300 text-xs font-bold">—</span>,
                },
                {
                  anahtar: 'durum', baslik: 'Durum', hizala: 'orta', siralanir: true,
                  baslikAlti: () => (
                    <FiltreKutusu>
                      <input placeholder="Durum..." value={kolF.durum} onChange={e => setKol('durum', e.target.value)} className={fi} />
                    </FiltreKutusu>
                  ),
                  ciz: r => {
                    const dc = durumConfig(r.durum);
                    return (
                      <span className={'inline-flex items-center gap-1.5 text-[9px] font-black px-2.5 py-1 rounded-xl uppercase tracking-widest whitespace-nowrap ' + dc.cls}>
                        <span className={'w-1.5 h-1.5 rounded-full flex-shrink-0 ' + dc.dot} />
                        {r.durum}
                      </span>
                    );
                  },
                },
                {
                  anahtar: 'islem', baslik: 'İşlem', hizala: 'sag',
                  ciz: r => (
                    <div className="relative">
                      <button onClick={e => {
                        e.stopPropagation();
                        if (acikDropdown === r.id) { setAcikDropdown(null); return; }
                        const rect = e.currentTarget.getBoundingClientRect();
                        setDropdownPos({ top: rect.top, bottom: rect.bottom, left: rect.left });
                        setAcikDropdown(r.id);
                      }} className="flex items-center gap-1.5 bg-slate-100 hover:bg-indigo-600 hover:text-white text-slate-600 text-[10px] px-4 py-2 rounded-xl font-black transition-all uppercase tracking-widest">
                        İşlem <span className="text-[8px]">▾</span>
                      </button>
                      <DropdownMenu r={r} acikDropdown={acikDropdown} setAcikDropdown={setAcikDropdown} dropdownPos={dropdownPos} handleIslem={handleIslem} />
                    </div>
                  ),
                },
              ]}
            />
          )}
        </div>

        {aktifTab !== 'konaklayanlar' && (
          <Pagination
            toplam={veriTum.length}
            sayfaBasi={sayfaBasi}
            sayfaBasiDegistir={setSayfaBasi}
            aktifSayfa={aktifSayfa}
            sayfaDegistir={setAktifSayfa}
          />
        )}
      </div>

      {/* ── Modaller ──────────────────────────────────────────────────────── */}
      {detayRez && (
        <RezervasyonDetayModal
          rezervasyonId={detayRez.rezervasyonId}
          onClose={() => setDetayRez(null)}
          onGuncelle={fetchRezervasyonlar}
        />
      )}
      {iptalRez && (
        <IptalOnayModal
          rezervasyon={iptalRez}
          onClose={() => setIptalRez(null)}
          onOnayla={async () => {
            try { await apiRequest(`rezervasyonlar/${iptalRez.rezervasyonId}/iptal`, 'PATCH'); fetchRezervasyonlar(); }
            catch (e) { alert(e?.message || 'İptal başarısız.'); }
            setIptalRez(null);
          }}
        />
      )}
      {importModal && (
        <TopluImportModal
          onClose={() => setImportModal(false)}
          onTamamlandi={() => { fetchRezervasyonlar(); }}
        />
      )}

      {/* ── Alt Bilgi ──────────────────────────────────────────────────────── */}
      <UsageInfo
        id="rezervasyon-liste-yardim"
        color="indigo"
        title="Nasıl Kullanılır?"
        description="Rezervasyon listesi sayfasında otelinizdeki tüm geçmiş, gelecek ve mevcut konaklamaları yönetebilirsiniz. Sekmeler ve tarih filtreleri ile detaylı analiz yapabilirsiniz."
        scenarios={[
          { title: "Giriş & Çıkış İşlemleri", content: "Giriş bekleyen veya çıkış yapacak misafirleri ilgili sekmelerden takip edebilir, hızlı işlem menüsü ile check-in yapabilirsiniz." },
          { title: "Detaylı Sorgulama", content: "Tarih aralığı seçip filtrele butonuna basarak belirli bir döneme ait tüm rezervasyonları listeleyebilir, tablo içindeki kolon bazlı arama alanlarını kullanabilirsiniz." },
          { title: "İşlem Menüsü", content: "Rezervasyonların yanındaki 'İşlem' butonuna tıklayarak detayları görüntüleyebilir, WhatsApp/E-posta gönderebilir veya iptal işlemi yapabilirsiniz." }
        ]}
      />

      <style>{`
        @keyframes fadeIn    { from { opacity:0; transform:translateY(6px); } to { opacity:1; transform:translateY(0); } }
        @keyframes modalIn   { from { opacity:0; transform:scale(0.96) translateY(8px); } to { opacity:1; transform:scale(1) translateY(0); } }
        @keyframes menuIn    { from { opacity:0; transform:translateY(-6px) scale(0.97); } to { opacity:1; transform:translateY(0) scale(1); } }
        .animate-fade-in     { animation: fadeIn 0.3s ease-out; }
        .animate-modal-in    { animation: modalIn 0.25s ease-out; }
      `}</style>
    </div>
  );
}

// ── Dropdown Menu ─────────────────────────────────────────────────────────────
// createPortal kullanılıyor — backdrop-blur-xl gibi filter CSS'i olan parent
// container'lar position:fixed için yeni bir containing block oluşturduğundan
// dropdown yanlış konumlanıyor. Portal ile doğrudan document.body'e render edilir.
function DropdownMenu({ r, acikDropdown, setAcikDropdown, dropdownPos, handleIslem }) {
  if (acikDropdown !== r.id) return null;

  const MENU_W = 224; // w-56
  // Sağ taşma kontrolü: butonun sol kenarından başla, ekran dışına çıkıyorsa sola kaydır
  const left = Math.min(dropdownPos.left, window.innerWidth - MENU_W - 8);
  const openUp = dropdownPos.bottom > window.innerHeight * 0.6;

  return createPortal(
    <>
      <div className="fixed inset-0 z-[60]" onClick={() => setAcikDropdown(null)} />
      <div className="fixed bg-white border border-slate-100 rounded-[2rem] shadow-2xl shadow-slate-200 z-[70] overflow-hidden"
        style={{
          width: MENU_W,
          animation: 'menuIn 0.15s ease-out',
          left,
          ...(openUp
            ? { bottom: window.innerHeight - dropdownPos.top + 6 }
            : { top: dropdownPos.bottom + 6 }),
        }}>
        <div className="px-4 pt-4 pb-2">
          <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-2">Rezervasyon</p>
          {[
            { key: 'detay',   icon: '🔍', label: 'Detayı Görüntüle' },
          ].map(item => (
            <button key={item.key} onClick={() => handleIslem(item.key, r)}
              className="w-full text-left px-3 py-2.5 text-xs font-black text-slate-700 hover:bg-indigo-50 hover:text-indigo-700 rounded-2xl flex items-center gap-2.5 transition-colors uppercase tracking-widest">
              <span>{item.icon}</span>{item.label}
            </button>
          ))}
          {!r.durum?.includes('İptal') && !r.durum?.includes('C-OUT') && (
            <button onClick={() => handleIslem('iptal', r)}
              className="w-full text-left px-3 py-2.5 text-xs font-black text-rose-600 hover:bg-rose-50 rounded-2xl flex items-center gap-2.5 transition-colors uppercase tracking-widest">
              <span>🔴</span>İptal Et
            </button>
          )}
        </div>
      </div>
    </>,
    document.body
  );
}
